/**
 * Outside-click guards for Radix Dialogs that contain Select / DropdownMenu /
 * Popover / Tooltip controls.
 *
 * Why this exists
 * ---------------
 * Radix Select (and friends) renders its dropdown content into a Portal that
 * sits OUTSIDE the Dialog's DOM subtree. On touch devices, when the user taps
 * empty space:
 *   1. Radix Select intercepts the tap, closes its own dropdown.
 *   2. The pointer event continues to propagate.
 *   3. The Dialog's outside-click detector sees a tap on body / overlay
 *      (NOT inside its content) and closes itself, blowing away the user's
 *      in-progress form.
 *
 * Fix: spread these props onto the Radix Dialog's `<DialogContent>`. We
 * preventDefault on outside events when EITHER:
 *   - the event target is inside a popper portal (rare — happens when tapping
 *     between items inside the dropdown), OR
 *   - any popper is currently open in the DOM at the time of the outside
 *     event. In that case the user is unambiguously dismissing the popper,
 *     not the dialog. The next outside click (after the popper has fully
 *     closed) will dismiss the dialog as expected.
 */

const SAFE_OUTSIDE_SELECTORS = [
  // Radix Select / DropdownMenu / Popover / ContextMenu / HoverCard all use
  // popper. Their portal content is wrapped in `[data-radix-popper-content-wrapper]`.
  "[data-radix-popper-content-wrapper]",
  "[data-radix-select-content]",
  "[data-radix-select-viewport]",
  "[data-radix-dropdown-menu-content]",
  "[data-radix-popover-content]",
  // Sonner toasts render in a top-level region.
  "[data-sonner-toaster]",
  "[data-sonner-toast]",
  // Any nested overlay (e.g. AlertDialog opened from inside the Dialog).
  "[role='dialog']:not([data-state='closed'])",
  "[role='listbox']",
  "[role='option']",
];

const isSafeOutsideTarget = (target) => {
  if (!target || !(target instanceof Element)) return false;
  return SAFE_OUTSIDE_SELECTORS.some((sel) => {
    try { return target.closest(sel); } catch { return false; }
  });
};

/**
 * True when ANY Radix popper-based component (Select dropdown, DropdownMenu,
 * Popover, ContextMenu, HoverCard) is currently open anywhere on the page.
 * Used to swallow the Dialog's outside-click handler when the user is
 * actually dismissing the popper — see the file-level docstring.
 */
const isAnyPopperOpen = () => {
  if (typeof document === "undefined") return false;
  const wrappers = document.querySelectorAll("[data-radix-popper-content-wrapper]");
  // The wrapper can briefly stick around during exit animations even when
  // the content is closed — only count wrappers whose content is still open.
  for (const w of wrappers) {
    const content = w.querySelector("[data-state]");
    if (!content || content.getAttribute("data-state") === "open") return true;
  }
  return false;
};

/**
 * Spread on `<DialogContent {...dialogContentOutsideGuards}>` to harden it
 * against the touch-device "tap empty space → modal closes" bug when the
 * dialog contains Radix Select / DropdownMenu / Popover / Toast.
 */
export const dialogContentOutsideGuards = {
  onPointerDownOutside: (e) => {
    const target = e.target || e?.detail?.originalEvent?.target;
    if (isSafeOutsideTarget(target) || isAnyPopperOpen()) e.preventDefault();
  },
  onInteractOutside: (e) => {
    const target = e.target || e?.detail?.originalEvent?.target;
    if (isSafeOutsideTarget(target) || isAnyPopperOpen()) e.preventDefault();
  },
};
